import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Target, Layers, BarChart3, CheckCircle2, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RESET_REDIRECT_DELAY_MS = 1200;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const resetToken = new URLSearchParams(window.location.search).get("token");
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const goMode = (m) => { setMode(m); setError(""); setInfo(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        setInfo("If an account exists for that email, a reset link is on its way. Check your inbox.");
        return;
      }
      if (mode === "reset") {
        await api.post("/auth/reset-password", { token: resetToken, password: form.password });
        setInfo("Password updated. You can now sign in.");
        setForm({ name: "", email: "", password: "" });
        setTimeout(() => { window.history.replaceState({}, "", "/"); goMode("login"); }, RESET_REDIRECT_DELAY_MS);
        return;
      }
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="h-16 flex items-center justify-between px-6 sm:px-10 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-emerald-500 flex items-center justify-center">
            <Target className="h-5 w-5 text-white" />
          </div>
          <span className="font-display font-bold text-lg text-slate-900">Performance Flow Pro</span>
        </div>
        <button
          data-testid="login-btn-header"
          onClick={handleLogin}
          className="bg-slate-900 text-white hover:bg-slate-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          Sign in
        </button>
      </header>

      <main className="flex-1 grid lg:grid-cols-2">
        <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-16">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 mb-4">
            ATD 6-Phase HPI · Nested ADDIE
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            Diagnose performance gaps.
            <span className="text-emerald-600"> Engineer the right fix.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-md leading-relaxed">
            Run consulting projects through ATD's Human Performance Improvement model — with a built-in ADDIE
            training engine that activates only when knowledge & skills are the real cause.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button
              data-testid="login-btn-hero"
              onClick={handleLogin}
              className="group bg-emerald-500 text-white hover:bg-emerald-600 px-6 py-3 rounded-md font-medium transition-colors flex items-center gap-2"
            >
              Continue with Google
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500">
            {["6-Phase HPI workflow", "Bloom's Taxonomy AI", "Pre vs post metrics & gap closed"].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {t}
              </span>
            ))}
          </div>
        </div>

        <div className="hidden lg:flex items-center justify-center bg-slate-900 relative overflow-hidden p-10">
          <img
            src="https://images.unsplash.com/photo-1704975986930-0c09f513c985?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-xl shadow-2xl p-7">
            <div className="flex items-center bg-slate-100 rounded-md p-1 mb-6">
              <button
                data-testid="tab-login"
                onClick={() => { setMode("login"); setError(""); }}
                className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${mode === "login" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
              >
                Sign in
              </button>
              <button
                data-testid="tab-register"
                onClick={() => { setMode("register"); setError(""); }}
                className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${mode === "register" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
              >
                Create account
              </button>
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "register" && (
                <div className="relative">
                  <UserIcon className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input data-testid="register-name" required value={form.name} onChange={update("name")} placeholder="Full name" className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 outline-none" />
                </div>
              )}
              {mode !== "reset" && (
                <div className="relative">
                  <Mail className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input data-testid="auth-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@company.com" className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 outline-none" />
                </div>
              )}
              {mode !== "forgot" && (
                <div className="relative">
                  <Lock className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input data-testid="auth-password" type="password" required value={form.password} onChange={update("password")} placeholder={mode === "register" || mode === "reset" ? "New password (min 6)" : "Password"} className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 outline-none" />
                </div>
              )}

              {mode === "login" && (
                <div className="text-right -mt-1">
                  <button type="button" data-testid="forgot-link" onClick={() => goMode("forgot")} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">Forgot password?</button>
                </div>
              )}

              {error && <p data-testid="auth-error" className="text-sm text-red-600">{error}</p>}
              {info && <p data-testid="auth-info" className="text-sm text-emerald-600">{info}</p>}

              <button data-testid="auth-submit" type="submit" disabled={loading} className="w-full bg-emerald-500 text-white hover:bg-emerald-600 py-2.5 rounded-md font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset" : "Update password"}
              </button>

              {(mode === "forgot" || mode === "reset") && (
                <button type="button" data-testid="back-to-login" onClick={() => goMode("login")} className="w-full text-xs text-slate-500 hover:text-slate-700">Back to sign in</button>
              )}
            </form>

            {mode !== "forgot" && mode !== "reset" && (
              <>
                <div className="flex items-center gap-3 my-4">
                  <div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">OR</span><div className="h-px bg-slate-200 flex-1" />
                </div>
                <button data-testid="google-login-btn" onClick={handleLogin} className="w-full border border-slate-300 hover:bg-slate-50 py-2.5 rounded-md font-medium text-sm text-slate-700 transition-colors">
                  Continue with Google
                </button>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
